import PropTypes from "prop-types";

import {
  Modal as MuiModal,
  Box,
  Typography,
  FormControl,
  TextField,
  Stack,
  Chip
} from "@mui/material";

import ModalContentBox from "@mui-theme/styled/ModalContentBox";
import CloseModalBox from "@mui-theme/styled/CloseModalBox";
import ThemedButton from "@mui-theme/styled/ThemedButton";

import useAddDeleteTagModal from "./useAddDeleteTagModal";

const AddDeleteTagModal = props => {
  const {
    open,
    action,
    tag,
    newTagName,
    setNewTagName,
    fetching,
    toggle,
    inputErrors,
    inputSuggestions,
    setInputSuggestions,
    selectedSuggestionIndex,
    setSelectedSuggestionIndex,
    lastUsedTags,
    handleKeyDown,
    canSubmitForm,
    serverMessage,
    handleInputChange,
    handleSubmit
  } = useAddDeleteTagModal(props);

  return (
    <MuiModal
      open={open}
      onClose={(_, reason) => {
        if (reason !== "backdropClick") {
          toggle();
        }
      }}
      sx={{
        overflow: "scroll"
      }}
    >
      <ModalContentBox
        sx={{
          maxWidth: 360,
          maxHeight: "96%",
          overflow: "visible"
        }}
      >
        <Box
          sx={{
            width: "100%",
            px: 2,
            alignItems: "flex-start"
          }}
        >
          <Typography variant="h4" color="common.black">
            {action === "add" ? "Lägg till tagg" : "Ta bort tagg"}
          </Typography>
        </Box>
        <Box
          sx={{
            width: "100%",
            alignItems: "flex-start",
            display: "flex",
            flexFlow: "column",
            px: 2
          }}
        >
          {action !== "add" && (
            <Typography variant="h6" sx={{ color: "primary.main", mb: "5px" }}>
              Är du säker på att du vill ta bort taggen "{tag}"?
            </Typography>
          )}
        </Box>
        {action === "add" && (
          <Box
            sx={{
              width: "100%",
              display: "flex",
              flexDirection: "column",
              justifyContent: "flex-start",
              alignItems: "flex-start",
              mt: 1,
              px: 2
            }}
          >
            <FormControl
              sx={{
                width: "100%",
                mr: { xs: 0, md: 1 }
              }}
            >
              <TextField
                autoFocus
                value={newTagName || ""}
                required
                fullWidth
                id="tagName"
                name="tagName"
                label="Taggnamn"
                autoComplete="tagName"
                error={!!inputErrors?.tagName}
                onChange={e => {
                  handleInputChange(e);
                }}
                onKeyDown={handleKeyDown}
                helperText={inputErrors?.tagName}
                sx={{
                  mb: 2
                }}
                inputProps={{ maxLength: 20 }}
              />
              {inputSuggestions.length > 0 && (
                <Box
                  sx={{
                    position: "absolute",
                    overflow: "hidden",
                    top: "58px",
                    width: "100%",
                    alignItems: "flex-start",
                    backgroundColor: "common.white",
                    border: "2px solid",
                    borderTopStyle: "dashed",
                    borderBottomLeftRadius: 8,
                    borderBottomRightRadius: 8,
                    borderColor: "primary.dark",
                    mt: -1,
                    mb: 1,
                    zIndex: 99999
                  }}
                >
                  {inputSuggestions.map((suggestion, index) => {
                    const suggestionFormatted =
                      suggestion.charAt(0).toUpperCase() +
                      suggestion.slice(1).toLowerCase();
                    return (
                      <Box
                        key={suggestion}
                        sx={{
                          width: "100%",
                          alignItems: "flex-start",
                          px: 1,
                          py: 1.5,
                          backgroundColor:
                            selectedSuggestionIndex === index
                              ? "secondary.dark"
                              : "common.white",
                          cursor: "pointer",
                          "&:hover": {
                            backgroundColor: "grey.200"
                          }
                        }}
                        onClick={() => {
                          setNewTagName(suggestionFormatted);
                          setInputSuggestions([]);
                          setSelectedSuggestionIndex(-1);
                        }}
                      >
                        <Typography variant="body1">{suggestionFormatted}</Typography>
                      </Box>
                    );
                  })}
                </Box>
              )}
              {lastUsedTags.length > 0 && (
                <Box sx={{ width: "100%", alignItems: "flex-start" }}>
                  <Typography
                    variant="body2"
                    sx={{
                      mb: "3px",
                      color: "text.secondary",
                      fontWeight: "bold"
                    }}
                  >
                    Senast använda:
                  </Typography>
                  <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap" }}>
                    {lastUsedTags.map((tag, index) => {
                      const tagLabel =
                        tag.charAt(0).toUpperCase() +
                        tag.slice(1).toLowerCase();

                      return (
                        <Chip
                          key={index}
                          label={tagLabel}
                          onClick={() => {
                            setNewTagName(tagLabel);
                          }}
                          sx={{
                            flexDirection: "row",
                            alignItems: "center",
                            height: "16px",
                            padding: 0,
                            pt: "3px",
                            marginBottom: "3px !important",
                            marginLeft:
                              index > 0 ? "8px !important" : "0 !important",
                            backgroundColor: "transparent",
                            color: "#000",
                            fontSize: "12px",
                            fontWeight: 400,
                            //remove borders execpt bottom dashed border
                            border: "none",
                            borderBottom: "1px dashed #999",
                            borderRadius: 0,
                            "& .MuiChip-label": {
                              px: "2px"
                            }
                          }}
                        />
                      );
                    })}
                  </Stack>
                </Box>
              )}
            </FormControl>
            {serverMessage && (
              <Typography
                variant="body2"
                color={serverMessage.type === "error" ? "error" : "primary"}
                sx={{ mt: 1 }}
              >
                {serverMessage.text}
              </Typography>
            )}
          </Box>
        )}
        <CloseModalBox
          sx={{
            mt: 1.25,
            px: 2
          }}
        >
          <ThemedButton
            variant="outlined"
            onClick={toggle}
            sx={{
              minWidth: "unset",
              width: 100
            }}
          >
            Avbryt
          </ThemedButton>
          <ThemedButton
            disabled={!canSubmitForm || fetching}
            variant="contained"
            loading={fetching}
            onClick={handleSubmit}
            sx={{
              minWidth: "unset",
              width: 150
            }}
          >
            {action === "add" ? "Lägg till" : "Radera"}
          </ThemedButton>
        </CloseModalBox>
      </ModalContentBox>
    </MuiModal>
  );
};

AddDeleteTagModal.propTypes = {
  open: PropTypes.bool,
  toggle: PropTypes.func,
  tag: PropTypes.string,
  client: PropTypes.object,
  userName: PropTypes.string,
  updateCustomers: PropTypes.func
};

export default AddDeleteTagModal;
